import { useEffect, useRef, useState } from "react";

import { ANIMATION_TIMINGS } from "../constants";
import { type ScoreChange } from "../types";

interface AnimationState {
  /**
   * Index of the current score change being animated (-1 means not started)
   */
  currentChangeIndex: number;
  /**
   * Whether the score counter is currently animating
   */
  isScoreAnimating: boolean;
  /**
   * Number of line items that should be visible
   */
  visibleLineItemCount: number;
  /**
   * Whether all animations have completed and button should be enabled
   */
  isAnimationComplete: boolean;
}

interface UseScoreAnimationSequenceResult {
  /**
   * The current end value for the score counter
   */
  currentEndScore: number;
  /**
   * Whether the score counter is currently animating
   */
  isScoreAnimating: boolean;
  /**
   * Number of line items that should be visible (0 = none, 1 = first, etc.)
   */
  visibleLineItemCount: number;
  /**
   * Whether the button should be enabled
   */
  isButtonEnabled: boolean;
}

export function useScoreAnimationSequence(
  startScore: number,
  scoreChanges: ScoreChange[]
): UseScoreAnimationSequenceResult {
  const [animationState, setAnimationState] = useState<AnimationState>({
    currentChangeIndex: -1,
    isScoreAnimating: false,
    visibleLineItemCount: 0,
    isAnimationComplete: false,
  });

  // Use ref to track current scoreChanges to avoid stale closures in nested timers
  const scoreChangesRef = useRef(scoreChanges);
  scoreChangesRef.current = scoreChanges;

  useEffect(() => {
    if (scoreChanges.length === 0) {
      setAnimationState({
        currentChangeIndex: -1,
        isScoreAnimating: false,
        visibleLineItemCount: 0,
        isAnimationComplete: true,
      });
      return;
    }

    const timers: NodeJS.Timeout[] = [];
    let isCancelled = false;

    function startScoreAnimation(index: number): void {
      setAnimationState((previous) => ({
        ...previous,
        currentChangeIndex: index,
        isScoreAnimating: true,
      }));
    }

    function showLineItem(index: number): void {
      setAnimationState((previous) => ({
        ...previous,
        isScoreAnimating: false,
        visibleLineItemCount: index + 1,
      }));
    }

    function markAnimationComplete(): void {
      setAnimationState((previous) => ({
        ...previous,
        isAnimationComplete: true,
      }));
    }

    function scheduleTimer(callback: () => void, delay: number): void {
      const timer = setTimeout(() => {
        if (!isCancelled) {
          callback();
        }
      }, delay);
      timers.push(timer);
    }

    function scheduleLineItemReveal(index: number): void {
      scheduleTimer(() => {
        showLineItem(index);

        const isLastChange = index >= scoreChangesRef.current.length - 1;
        if (isLastChange) {
          markAnimationComplete();
        } else {
          scheduleNextChange(index + 1);
        }
      }, ANIMATION_TIMINGS.SCORE_ANIMATION_DURATION_MS + ANIMATION_TIMINGS.LINE_ITEM_DELAY_MS);
    }

    function scheduleNextChange(index: number): void {
      scheduleTimer(() => {
        startScoreAnimation(index);
        scheduleLineItemReveal(index);
      }, ANIMATION_TIMINGS.BETWEEN_CHANGES_DELAY_MS);
    }

    // Start the animation sequence after initial delay
    scheduleTimer(() => {
      startScoreAnimation(0);
      scheduleLineItemReveal(0);
    }, ANIMATION_TIMINGS.INITIAL_DELAY_MS);

    return () => {
      isCancelled = true;
      timers.forEach((timer) => {
        clearTimeout(timer);
      });
    };
  }, [scoreChanges]);

  // Calculate current end score based on animation state
  const currentEndScore =
    animationState.currentChangeIndex < 0
      ? startScore
      : scoreChanges
          .slice(0, animationState.currentChangeIndex + 1)
          .reduce((sum, change) => sum + change.value, startScore);

  return {
    currentEndScore,
    isScoreAnimating: animationState.isScoreAnimating,
    visibleLineItemCount: animationState.visibleLineItemCount,
    isButtonEnabled: animationState.isAnimationComplete,
  };
}
